import { configuration } from 'src/configuration';
import { UserDataSourceRole } from 'src/types/data-sources';
import { OrganizationResearcherRole } from 'src/types/organizations';
import { UserStudyRole } from 'src/types/studies';
import { UserGroup } from 'src/types/users';

const { accountApiUrl } = configuration;

export const getUsers = async ({
  token,
  search,
  after,
}: {
  search: string;
  token: string;
  after: string;
}) => {
  const response = await fetch(`${accountApiUrl}/users?search=${search}&limit=15&after=${after}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  const data = await response.json();

  if (data && data.users) {
    return { users: data.users, lastEvaluatedKey: data.lastEvaluatedKey };
  }

  return { users: [], lastEvaluatedKey: null };
};

export const getUserDetails = async ({ token, id }: { token: string; id: string }) => {
  const response = await fetch(`${accountApiUrl}/users/${id}/details`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  const data = await response.json();

  return data.details;
};

export const getUserById = async ({ token, id }: { token: string; id: string }) => {
  const response = await fetch(`${accountApiUrl}/users/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  const data = await response.json();

  return data.user;
};

export interface CreateUserDTO {
  email: string;
  phoneNumber: string;
  givenName: string;
  familyName: string;
  group: string;
}

export const createUser = async (token: string, createUserDto: CreateUserDTO) => {
  const response = await fetch(`${accountApiUrl}/users`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    method: 'POST',
    body: JSON.stringify({ ...createUserDto }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  const data = await response.json();

  return data.user;
};
export interface UpdateUserDTO {
  userId: string;
  email: string;
  phoneNumber: string;
  givenName: string;
  familyName: string;
}

export const updateUser = async ({
  token,
  updatedUser,
}: {
  token: string;
  updatedUser: UpdateUserDTO;
}) => {
  const response = await fetch(`${accountApiUrl}/users/${updatedUser.userId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    method: 'PATCH',
    body: JSON.stringify({ ...updatedUser }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  const data = await response.json();

  return data.user;
};

export const deleteUser = async ({ id, token }: { id: string; token: string }) => {
  const response = await fetch(`${accountApiUrl}/users/${id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }
};

export interface UserActionDTO {
  type: string;
  id: string;
}

export const userAction = async (token: string, { id, type }: UserActionDTO) => {
  const actionUrl = type === 'confirm' ? 'confirm' : 'reset-password';
  const response = await fetch(`${accountApiUrl}/users/${id}/${actionUrl}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  const user = await response.json();

  return user;
};

export const changePassword = async (
  token: string,
  {
    accessToken,
    oldPassword,
    newPassword,
  }: { accessToken: string; oldPassword: string; newPassword: string },
) => {
  const response = await fetch(`${accountApiUrl}/users/change-password`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    method: 'POST',
    body: JSON.stringify({ accessToken, oldPassword, newPassword }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  return 'Success';
};

interface AddUserToOrganizationDTO {
  token: string;
  userId: string;
  organizationId: string;
  role: OrganizationResearcherRole;
}

export const addUserToOrganization = async ({
  token,
  userId,
  organizationId,
  role,
}: AddUserToOrganizationDTO) => {
  const response = await fetch(`${accountApiUrl}/organizations/${organizationId}/users/${userId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    method: 'POST',
    body: JSON.stringify({ role }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  return 'Success';
};

interface AddUserToStudyDTO {
  token: string;
  userId: string;
  studyId: string;
  role: UserStudyRole;
}

export const addUserToStudy = async ({ token, userId, studyId, role }: AddUserToStudyDTO) => {
  const response = await fetch(`${accountApiUrl}/studies/${studyId}/users/${userId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    method: 'POST',
    body: JSON.stringify({ role })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  return 'Success';
};

interface AddUserToDataSourceDTO {
  token: string;
  userId: string;
  dataSourceId: string;
  role: UserDataSourceRole;
}

interface AddUserToCohortDTO extends AddUserToDataSourceDTO {
  cohortId: string;
}

export const addUserToDataSource = async ({
  token,
  userId,
  dataSourceId,
  role,
}: AddUserToDataSourceDTO) => {
  const response = await fetch(`${accountApiUrl}/data-sources/${dataSourceId}/users/${userId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    method: 'POST',
    body: JSON.stringify({ role }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  return 'Success';
};

export const addUserToCohort = async ({
  token,
  userId,
  dataSourceId,
  cohortId,
  role,
}: AddUserToCohortDTO) => {
  const response = await fetch(
    `${accountApiUrl}/data-sources/${dataSourceId}/cohort/${cohortId}/users/${userId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({ role }),
    },
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  return 'Success';
};

export const removeUserFromStudy = async ({
  token,
  userId,
  studyId,
  role,
}: {
  token: string;
  userId: string; // user id
  studyId: string;
  role: UserStudyRole;
}) => {
  const response = await fetch(`${accountApiUrl}/studies/${studyId}/users/${userId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    method: 'DELETE',
    body: JSON.stringify({ role }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }
};

export const removeUserFromDataSource = async ({
  token,
  userId,
  dataSourceId,
  role,
}: {
  token: string;
  userId: string;
  dataSourceId: string;
  role: UserDataSourceRole;
}) => {
  const response = await fetch(`${accountApiUrl}/data-sources/${dataSourceId}/users/${userId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    method: 'DELETE',
    body: JSON.stringify({ role }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }
};

export const removeUserFromCohort = async ({
  token,
  userId,
  dataSourceId,
  cohortId,
  role,
}: {
  token: string;
  userId: string;
  dataSourceId: string;
  cohortId: string;
  role: UserDataSourceRole;
}) => {
  const response = await fetch(
    `${accountApiUrl}/data-sources/${dataSourceId}/cohort/${cohortId}/users/${userId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      method: 'DELETE',
      body: JSON.stringify({ role }),
    },
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }
};

export const resendInvitation = async ({ token, id }: { token: string; id: string }) => {
  const response = await fetch(`${accountApiUrl}/users/${id}/resend-invitation`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  return 'Success';
};

export const modifyUserRoles = async ({
  token,
  id,
  groups,
}: {
  token: string;
  id: string;
  groups: UserGroup[];
}) => {
  const response = await fetch(`${accountApiUrl}/users/${id}/groups`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    method: 'PUT',
    body: JSON.stringify({ groups }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  return 'Success';
};

export const getUserOverview = async ({ token, userId }: { token: string; userId: string }) => {
  const response = await fetch(`${accountApiUrl}/users/${userId}/overview`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  const data = await response.json();

  return data;
};

export const getResearcherOrganizations = async ({
  token,
  researcherId,
}: {
  token: string;
  researcherId: string;
}) => {
  const response = await fetch(`${accountApiUrl}/researchers/${researcherId}/organizations`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  const data = await response.json();

  return data;
};

export const verifyUserAttribute = async ({
  token,
  accessToken,
  attributeName,
  code,
}: {
  token: string;
  accessToken: string;
  attributeName: string;
  code: string;
}) => {
  const response = await fetch(`${accountApiUrl}/users/verify-user-attribute`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    method: 'POST',
    body: JSON.stringify({
      accessToken,
      attributeName,
      code,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  return 'Success';
};

export const generateVerificationCode = async ({
  token,
  accessToken,
  attributeName
}: {
  token: string;
  accessToken: string;
  attributeName: string;
}) => {
  const response = await fetch(`${accountApiUrl}/users/generate-user-attribute-verification-code`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    method: 'POST',
    body: JSON.stringify({
      accessToken,
      attributeName
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  return 'Success';
}